import { Box, Button, Stack, Typography, CircularProgress } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useTranslation } from 'react-i18next';
import type { Notification } from '@altyor-kronos/shared-types';
import NotificationCard from './NotificationCard';

interface NotificationFeedProps {
  notifications: Notification[];
  loading: boolean;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  /** Max items to display (undefined = show all). */
  limit?: number;
  /** When limited, show a "View all" link. */
  onViewAll?: () => void;
}

export default function NotificationFeed({
  notifications,
  loading,
  onMarkAsRead,
  onMarkAllAsRead,
  limit,
  onViewAll,
}: NotificationFeedProps) {
  const { t } = useTranslation();
  const hasUnread = notifications.some((n) => !n.read);
  const visible = limit ? notifications.slice(0, limit) : notifications;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (notifications.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {t('notifications.empty')}
      </Typography>
    );
  }

  return (
    <Box>
      {hasUnread && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            size="small"
            startIcon={<DoneAllIcon />}
            onClick={onMarkAllAsRead}
          >
            {t('notifications.markAllRead')}
          </Button>
        </Box>
      )}
      <Stack spacing={1}>
        {visible.map((n) => (
          <NotificationCard
            key={n.id}
            notification={n}
            onMarkAsRead={onMarkAsRead}
          />
        ))}
      </Stack>
      {limit && notifications.length > limit && onViewAll && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" onClick={onViewAll}>
            {t('notifications.viewAll')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
